import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchBancos, deleteBanco, setCurrentBanco } from '../../reducers/banco_slice';
import BancoForm from './BancoForm';
import Table from 'react-bootstrap/Table';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import ActionButton from '../UI/Buttons/ActionButton';
import CustomPrintModal from '../UI/CustomPrintModal';

const BancoList = () => {
  const dispatch = useDispatch();
  const bancoState = useSelector(state => state.banco || { bancos: [] });
  const bancos = bancoState.bancos || [];
  const [showModal, setShowModal] = useState(false);
  const [currentBanco, setCurrentBanco] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [] });
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchBancos());
  }, [dispatch]);

  const handleDelete = id => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: "¡No podrás revertir esto!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#464646',
      confirmButtonText: 'Sí, eliminarlo!',
       cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteBanco(id)).unwrap().then(() => {
          Swal.fire('¡Eliminado!', 'El banco ha sido eliminado.', 'success');
        }).catch((error) => {
          Swal.fire('Error', error.error || 'Ocurrió un error al eliminar el banco', 'error');
        });
      }
    });
  };

  const handleSort = (key) => {
    if (sortConfig.direction === 'desc') {
      setSortConfig({ key: null, direction: 'asc' });
      return;
    }

    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = null; // Si ya estaba descendente, lo dejamos sin ordenar
    }

    setSortConfig({ key, direction });
  };

  const handleEdit = banco => {
    setCurrentBanco(banco);
    setShowModal(true); // Muestra el modal al editar
  };

  const handleCreate = () => {
    setCurrentBanco(null);
    setShowModal(true); // Muestra el modal al crear un nuevo banco
  };

  const handleClose = () => {
    setShowModal(false);
    setCurrentBanco(null);
    dispatch(fetchBancos());

  };

  const handleBack = () => {
    navigate('/admin');
  };

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(sortedBancos);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bancos");
    XLSX.writeFile(workbook, "bancos.xlsx");
  };

  const sortedBancos = useMemo(() => {
    let sortedData = [...bancos];
    if (sortConfig.key) {
      sortedData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortedData;
  }, [bancos, sortConfig]);

  const handleShowPreview = () => {
    setPrintContent(getPreviewContent());
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
  };

  const getPreviewContent = () => {
    const content = {
      head: [['N°', 'Banco', 'Cuenta Contable', 'Cuenta Bancaria', 'Estado']],
      body: sortedBancos.map((banco, index) => [
        index+1,
        banco.banco,
        banco.cuenta_contable,
        banco.cuenta_bancaria,
        banco.estado
      ])
    };
    return content;
  };

  return (
    <div className="list-container list0">
      <h1 className='table-title'>Lista de Bancos</h1>
      <div className="list-content">
        <div className='list-controls'>
          <div className="col-md-6">
          </div>
          <div className="col-md-6 text-right">
            <ActionButton onClick={handleCreate} outline icon='plus' floatEnd>
              Nuevo Banco
            </ActionButton>
          </div>
        </div>
        <div className="list-container-header">
          <div className="left-buttons">
            <button title="Volver" onClick={handleBack} className="icon-button btn-transparent"><i className="bi bi-arrow-left"></i></button>
          </div>
          <div className="right-buttons">
            <button title="Imprimir" onClick={handleShowPreview} className="icon-button btn-transparent"><i className="bi bi-printer"></i></button>
            <button title="Exportar a excel" onDoubleClick={handleExport} className="icon-button btn-transparent"><i className="bi bi-file-spreadsheet"></i></button>
          </div>
        </div>
        {bancos.length === 0 ? (
          <p>No hay bancos disponibles.</p>
        ) : (
          <div className='table-container'>
            <Table bordered hover size='sm'>
              <thead style={{backgroundColor:"#b2aea3"}}>
                <tr>
                  <th className='col-index'>N°</th>
                  <th onClick={() => handleSort('banco')}>
                    Banco
                    {sortConfig.key === 'banco' && (
                      sortConfig.direction === 'asc' ? <i className="bi bi-arrow-up"></i> : <i className="bi bi-arrow-down"></i>
                    )}
                  </th>
                  <th>Cuenta Contable</th>
                  <th>Cuenta Bancaria</th>
                  <th>Estado</th>
                  <th className='col-simple-actions'>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sortedBancos.map((banco, index) => (
                  <tr key={banco.id}>
                    <td>{index + 1}</td>
                    <td>{banco.banco}</td>
                    <td>{banco.cuenta_contable}</td>
                    <td>{banco.cuenta_bancaria}</td>
                    <td>{banco.estado}</td>
                    <td>
                      <button className="icon-button btn-secondary-outline" title="Editar" onClick={() => handleEdit(banco)}>
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className="icon-button btn-secondary-outline" title="Eliminar" onClick={() => handleDelete(banco.id)}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {/* Renderizamos el modal solo cuando es necesario */}
        {showModal && (
          <BancoForm banco={currentBanco} show={showModal} handleClose={handleClose} />
        )}
        <CustomPrintModal
          show={showPreviewModal}
          handleClose={handleClosePreview}
          title1="LISTA DE BANCOS"
          title2=""
          content={printContent}
          copia={false}
        />
      </div>
    </div>
  );
};

export default BancoList;
